import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/utils/cn';
import { useAuth } from '@/contexts/AuthContext';
import { canAccessAdminPanel } from '@/utils/adminAccess';
import {
  HomeIcon,
  UserGroupIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  MapPinIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  DocumentTextIcon,
  BuildingLibraryIcon,
  ShieldExclamationIcon,
  DocumentMagnifyingGlassIcon,
  Cog6ToothIcon,
  RectangleStackIcon,
  UserIcon,
} from '@heroicons/react/24/outline';

interface NavigationSection {
  section: string;
  /** Se true, a seção só aparece para usuários com role ADMIN */
  adminOnly?: boolean;
  items: {
    name: string;
    href: string;
    icon: React.ComponentType<{ className?: string }>;
  }[];
}

const navigation: NavigationSection[] = [
  {
    section: 'Principal',
    items: [
      { name: 'Dashboard', href: '/', icon: HomeIcon },
    ],
  },
  {
    section: 'Administração',
    adminOnly: true,
    items: [
      { name: 'Painel administrativo', href: '/admin', icon: RectangleStackIcon },
    ],
  },
  {
    section: 'Cadastros Base',
    items: [
      { name: 'Cidades', href: '/cidades', icon: MapPinIcon },
      { name: 'Instituições', href: '/instituicoes', icon: BuildingLibraryIcon },
      { name: 'Cursos', href: '/cursos', icon: AcademicCapIcon },
      { name: 'Seguradoras', href: '/seguradoras', icon: ShieldExclamationIcon },
      { name: 'Agentes Integradores', href: '/agentes-integradores', icon: BuildingOfficeIcon },
    ],
  },
  {
    section: 'Gestão',
    items: [
      { name: 'Alunos', href: '/alunos', icon: UserGroupIcon },
      { name: 'Empresas', href: '/empresas', icon: BuildingOfficeIcon },
      { name: 'Estágios', href: '/estagios', icon: ClipboardDocumentListIcon },
      { name: 'Horários', href: '/horarios', icon: ClockIcon },
    ],
  },
  {
    section: 'Gerar',
    items: [
      { name: 'Termo de Estágio', href: '/termo-contrato', icon: DocumentMagnifyingGlassIcon },
      { name: 'Termo de Rescisão', href: '/termo-rescisao', icon: DocumentMagnifyingGlassIcon },
    ],
  },
  {
    section: 'Documentos',
    items: [
      { name: 'Relatórios de Estágio', href: '/relatorios', icon: DocumentTextIcon },
      { name: 'Gerenciar Relatórios', href: '/relatorios/gerenciar', icon: DocumentTextIcon },
    ],
  },
  {
    section: 'Relatório Gerencial',
    items: [
      { name: 'Relatório Personalizado', href: '/relatorios/personalizados', icon: DocumentTextIcon },
    ],
  },
  {
    section: 'Configurações',
    items: [
      { name: 'Meu perfil', href: '/perfil', icon: UserIcon },
      { name: 'Configuração de Termo', href: '/configuracao-termo', icon: Cog6ToothIcon },
    ],
  },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isMobile?: boolean;
  isTablet?: boolean;
}

export const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose, isMobile = false, isTablet = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = canAccessAdminPanel(user);
  const userName = user?.nome?.trim() || 'Administrador';
  const userInitial = userName.charAt(0).toUpperCase();

  const visibleSections = navigation.filter((section) => !section.adminOnly || isAdmin);

  return (
    <>
      {/* Sidebar */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out',
          // Desktop: sempre visível
          'lg:translate-x-0 lg:static lg:inset-0',
          // Mobile/Tablet: controlado por isOpen
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <div className="flex h-full flex-col">
          {/* Logo */}
          <div className="flex h-16 items-center justify-center border-b border-gray-200 px-4">
            <Link to="/" className="flex items-center justify-center">
              <img 
                src="/imagem/logo-nome.png" 
                alt="Systagio Logo" 
                className="h-24 w-auto object-contain"
              />
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 overflow-y-auto">
            {visibleSections.map((section, sectionIdx) => (
              <div key={section.section} className={sectionIdx > 0 ? 'mt-6' : ''}>
                <h3 className="px-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  {section.section}
                </h3>
                <div className="space-y-1">
                  {section.items.map((item) => {
                    const isActive =
                      item.href === '/'
                        ? location.pathname === '/'
                        : item.href === '/admin'
                          ? location.pathname.startsWith('/admin')
                          : location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        className={cn(
                          'group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors',
                          isActive
                            ? 'bg-primary-100 text-primary-700'
                            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                        )}
                        onClick={() => {
                          // Close sidebar on mobile/tablet when navigating
                          if (isMobile || isTablet) {
                            onClose();
                          }
                        }}
                      >
                        <item.icon
                          className={cn(
                            'mr-3 h-5 w-5 flex-shrink-0',
                            isActive ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                          )}
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          {/* User info */}
          <div className="border-t border-gray-200 p-4">
            <button
              type="button"
              className="flex w-full items-center rounded-md p-1 text-left transition-colors hover:bg-gray-100"
              onClick={() => {
                navigate('/perfil');
                if (isMobile || isTablet) {
                  onClose();
                }
              }}
              aria-label="Abrir perfil do usuário"
            >
              <div className="flex-shrink-0">
                <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                  <span className="text-sm font-medium text-primary-700">{userInitial}</span>
                </div>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-gray-700">{userName}</p>
              </div>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
